import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync } from "../../utilities/coalesceAsync";

const resolveList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data?.data)) {
    return payload.data.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

export const GetDeliveryTypes = createAsyncThunk(
  "deliveryTypes/getAll",
  async (token, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const data = await coalesceAsync(
        "get-delivery-types",
        async () => {
          const response = await fetch(`${config.apiUrl}/delivery-types`, {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          const body = await response.json();

          if (!response.ok) {
            const error = new Error(body?.message ?? "Unable to fetch delivery types");
            error.payload = body;
            throw error;
          }

          return body;
        },
        { ttlMs: 1500 }
      );

      return fulfillWithValue(data);
    } catch (error) {
      if (error?.payload) {
        return rejectWithValue(error.payload);
      }
      return rejectWithValue({
        message: error?.message ?? "Unable to fetch delivery types",
      });
    }
  }
);

export const SetDefaultDeliveryType = createAsyncThunk(
  "deliveryTypes/setDefault",
  async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/delivery-types/${id}/set-default`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Failed to set default" });
    }
  }
);

export const UpdateDeliveryType = createAsyncThunk(
  "deliveryTypes/update",
  async ({ token, id, payload }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await fetch(`${config.apiUrl}/delivery-types/${id}`, {
        method: "PUT",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) return rejectWithValue(data);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({ message: error?.message ?? "Failed to update" });
    }
  }
);

const initialState = {
  deliveryTypes: [],
  loading: false,
  error: null,
  successMessage: null,
};

const DeliveryTypesSlice = createSlice({
  name: "deliveryTypes",
  initialState,
  reducers: {
    clearDeliveryTypeFeedback: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetDeliveryTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetDeliveryTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.deliveryTypes = resolveList(action.payload);
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetDeliveryTypes.rejected, (state, action) => {
        state.loading = false;
        state.deliveryTypes = [];
        state.error = formatApiError(action.payload, "Unable to fetch delivery types");
        state.successMessage = null;
      })
      .addCase(SetDefaultDeliveryType.fulfilled, (state, action) => {
        const updated = action.payload?.data;
        if (updated) {
          state.deliveryTypes = state.deliveryTypes.map((dt) =>
            dt.id === updated.id ? { ...dt, ...updated, is_default: true } : { ...dt, is_default: false }
          );
        }
      })
      .addCase(UpdateDeliveryType.fulfilled, (state, action) => {
        const updated = action.payload?.data;
        if (updated) {
          state.deliveryTypes = state.deliveryTypes.map((dt) => (dt.id === updated.id ? { ...dt, ...updated } : dt));
        }
      });
  },
});

export default DeliveryTypesSlice.reducer;
export const { clearDeliveryTypeFeedback } = DeliveryTypesSlice.actions;
